import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DashboardSection } from './DashboardSection';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { AdminSnapshotCell } from '../../types/dashboard';

const Panel = styled(DashboardSection)``;

const PanelTitle = styled(Heading)`
  margin: 0 0 ${spacing[5]} 0 !important;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: ${breakpoints.md}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const MetricCell = styled.div<{ $clickable?: boolean }>`
  padding: ${spacing[4]} ${spacing[5]};
  border-right: 1px solid ${props => props.theme.colors.border};
  border-bottom: 1px solid ${props => props.theme.colors.border};

  @media (min-width: ${breakpoints.md}) {
    border-bottom: none;
    &:last-child {
      border-right: none;
    }
  }

  @media (max-width: ${breakpoints.md}) {
    &:nth-child(2n) {
      border-right: none;
    }
    &:nth-last-child(-n + 2) {
      border-bottom: none;
    }
  }

  ${props =>
    props.$clickable &&
    `
    cursor: pointer;
    transition: background 0.15s ease;
    &:hover {
      background: ${props.theme.colors.surfaceHover};
    }
  `}
`;

const MetricValue = styled.div`
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
`;

const MetricLabel = styled(Text)`
  display: block;
  margin-top: ${spacing[2]};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  font-size: ${typography.fontSize.xs};
`;

const CellLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

interface AdminSnapshotPanelProps {
  cells: AdminSnapshotCell[];
}

export const AdminSnapshotPanel: React.FC<AdminSnapshotPanelProps> = ({ cells }) => {
  const { t } = useTranslation();

  return (
    <Panel>
      <PanelTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
        {t('admin.home.snapshot.title')}
      </PanelTitle>
      <MetricsGrid>
        {cells.map(cell => {
          const content = (
            <>
              <MetricValue>{cell.value}</MetricValue>
              <MetricLabel size="xs" color="secondary">
                {cell.label}
              </MetricLabel>
            </>
          );

          if (cell.link) {
            return (
              <MetricCell key={cell.id} $clickable>
                <CellLink to={cell.link}>{content}</CellLink>
              </MetricCell>
            );
          }

          return <MetricCell key={cell.id}>{content}</MetricCell>;
        })}
      </MetricsGrid>
    </Panel>
  );
};

export default AdminSnapshotPanel;
